import { getApiUrl } from '../utils/config';

export interface UserSummary {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  is_active: boolean;
  is_admin: boolean;
  is_beta_tester: boolean;
  is_deleted?: boolean;
  credits: number;
  tier_name?: string;
  tier_display_name?: string;
  created_at: string;
  last_login?: string;
  total_posts: number;
  total_media_files: number;
  total_ai_tasks: number;
  clients?: Array<{
    id: string;
    name: string;
    slug: string;
    role: string;
  }>;
}

export interface UserDetail extends UserSummary {
  phone?: string;
  profile_picture_url?: string;
  updated_at: string;
  current_month_ai_tasks: number;
  current_month_posts: number;
  current_month_media_uploads: number;
  total_credits_used: number;
  recent_login_ips: string[];
  recent_activity_count: number;
}

export interface UserUpdate {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  is_active?: boolean;
  is_admin?: boolean;
  tier_id?: string;
}

export interface UserCreditsUpdate {
  credits: number;
  reason: string;
}

export interface UserSuspension {
  is_suspended: boolean;
  suspension_reason?: string;
  suspension_until?: string;
}

export interface UserSearchFilters {
  search?: string;
  tier_id?: string;
  is_active?: boolean;
  is_admin?: boolean;
  is_suspended?: boolean;
  created_after?: string;
  created_before?: string;
  last_login_after?: string;
  last_login_before?: string;
  min_credits?: number;
  max_credits?: number;
}

export interface UserListResponse {
  users: UserSummary[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface BulkUserAction {
  user_ids: string[];
  action: 'activate' | 'deactivate' | 'suspend' | 'delete' | 'add_credits' | 'remove_credits';
  value?: string;
  reason?: string;
}

export interface UserStatsResponse {
  total_users: number;
  active_users: number;
  suspended_users: number;
  admin_users: number;
  users_by_tier: Record<string, number>;
  new_users_this_month: number;
  active_users_this_week: number;
  total_credits_distributed: number;
  average_credits_per_user: number;
}

class AdminUserService {
  private getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  async getUsers(
    page: number = 1,
    perPage: number = 20,
    filters: UserSearchFilters = {},
    sortBy: string = 'created_at',
    sortOrder: 'asc' | 'desc' = 'desc'
  ): Promise<UserListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
      sort_by: sortBy,
      sort_order: sortOrder,
    });

    // Add filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await fetch(`${getApiUrl()}/api/v1/admin/users?${params}`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch users');
    }

    return response.json();
  }

  async getUserDetail(userId: string): Promise<UserDetail> {
    const response = await fetch(`${getApiUrl()}/api/v1/admin/users/${userId}`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user details');
    }

    return response.json();
  }

  async updateUser(userId: string, userData: UserUpdate): Promise<UserDetail> {
    const response = await fetch(`${getApiUrl()}/api/v1/admin/users/${userId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update user');
    }

    return response.json();
  }

  async updateUserCredits(userId: string, creditsData: UserCreditsUpdate): Promise<any> {
    const response = await fetch(`${getApiUrl()}/api/v1/admin/users/${userId}/credits`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(creditsData),
    });

    if (!response.ok) {
      throw new Error('Failed to update user credits');
    }

    return response.json();
  }

  async updateUserSuspension(userId: string, suspensionData: UserSuspension): Promise<any> {
    const response = await fetch(`${getApiUrl()}/api/v1/admin/users/${userId}/suspension`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(suspensionData),
    });

    if (!response.ok) {
      throw new Error('Failed to update user suspension');
    }

    return response.json();
  }

  async deleteUser(userId: string): Promise<any> {
    const response = await fetch(`${getApiUrl()}/api/v1/admin/users/${userId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to delete user');
    }

    return response.json();
  }

  async bulkUserAction(actionData: BulkUserAction): Promise<any> {
    const response = await fetch(`${getApiUrl()}/api/v1/admin/users/bulk-action`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(actionData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to perform bulk action');
    }

    return response.json();
  }

  async getUserStats(): Promise<UserStatsResponse> {
    const response = await fetch(`${getApiUrl()}/api/v1/admin/users/stats`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user statistics');
    }

    return response.json();
  }

  async createDefaultClientForUser(userId: string): Promise<any> {
    const response = await fetch(`${getApiUrl()}/api/v1/admin/users/${userId}/create-default-client`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create default client');
    }

    return response.json();
  }


  async toggleBetaTesterStatus(userId: string, isBetaTester: boolean): Promise<any> {
    const response = await fetch(`${getApiUrl()}/api/v1/admin/users/${userId}/beta-tester?is_beta_tester=${isBetaTester}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to toggle beta tester status');
    }

    return response.json();
  }
}

export default new AdminUserService();
